/**
 * WorkflowList Page
 *
 * Displays all workflows with filtering and management actions.
 */
import { BrandMark } from '../components/BrandMark';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { UserMenu } from '../components/common/UserMenu';
import { Button } from '../components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { LoadingSkeleton } from '../components/common/LoadingSkeleton';
import {
  useWorkflows,
  useWorkflowStats,
  useDeleteWorkflow,
  useActivateWorkflow,
  usePauseWorkflow,
  getWorkflowStatusColor,
  getWorkflowStatusIcon,
  getExecutionModeIcon,
  getExecutionModeLabel,
  getTriggerTypeIcon,
  getTriggerTypeLabel,
} from '../hooks/useWorkflows';
import { Workflow, WorkflowStatus } from '../types/workflow';
import { RunWorkflowModal } from '../components/workflow/RunWorkflowModal';

export default function WorkflowList() {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const [statusFilter, setStatusFilter] = useState<WorkflowStatus | ''>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);

  const { data, isLoading, error } = useWorkflows({
    page,
    limit: 12,
    status: statusFilter || undefined,
    search: searchQuery || undefined,
  });

  const { data: stats, isLoading: isLoadingStats } = useWorkflowStats();

  const workflows = data?.data || [];
  const meta = data?.meta;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Navigation Header */}
      <header className="border-b bg-white sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-6">
            <Link to="/dashboard" className="flex items-center space-x-2 hover:opacity-80 transition-opacity">
              <BrandMark className="h-7 w-7" />
              <span className="text-xl font-bold">AgentMesh</span>
            </Link>
            <nav className="hidden md:flex items-center space-x-4">
              <Link
                to="/dashboard"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Dashboard
              </Link>
              <Link
                to="/agents"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Agents
              </Link>
              <Link
                to="/workflows"
                className="text-sm font-medium text-foreground"
              >
                Workflows
              </Link>
              <Link
                to="/executions"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Executions
              </Link>
              <Link
                to="/templates"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Marketplace
              </Link>
            </nav>
          </div>
          <div className="flex items-center space-x-4">
            {user && (
              <span className="text-sm text-muted-foreground hidden sm:inline">
                {user.email}
              </span>
            )}
            <UserMenu />
          </div>
        </div>
      </header>

      <div className="p-4">
        <div className="max-w-7xl mx-auto py-8">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div>
              <h1 className="text-3xl font-bold">Workflows</h1>
              <p className="text-muted-foreground">
                Orchestrate multi-agent workflows
              </p>
            </div>
            <Button onClick={() => navigate('/workflows/new')}>
              <span className="mr-2">+</span>
              Create Workflow
            </Button>
          </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
          <StatsCard
            title="Total"
            value={stats?.total || 0}
            icon="🔄"
            isLoading={isLoadingStats}
          />
          <StatsCard
            title="Active"
            value={stats?.active || 0}
            icon="✅"
            color="green"
            isLoading={isLoadingStats}
          />
          <StatsCard
            title="Draft"
            value={stats?.draft || 0}
            icon="📝"
            color="gray"
            isLoading={isLoadingStats}
          />
          <StatsCard
            title="Paused"
            value={stats?.paused || 0}
            icon="⏸️"
            color="yellow"
            isLoading={isLoadingStats}
          />
          <StatsCard
            title="Total Runs"
            value={stats?.totalRuns || 0}
            icon="🚀"
            color="blue"
            isLoading={isLoadingStats}
          />
          <StatsCard
            title="Success Rate"
            value={stats?.successRate ? `${Math.round(stats.successRate)}%` : '-'}
            icon="📊"
            color="purple"
            isLoading={isLoadingStats}
          />
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="py-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <input
                  type="text"
                  placeholder="Search workflows..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setPage(1);
                  }}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div className="flex gap-2">
                <select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value as WorkflowStatus | '');
                    setPage(1);
                  }}
                  className="px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">All Statuses</option>
                  <option value="DRAFT">Draft</option>
                  <option value="ACTIVE">Active</option>
                  <option value="PAUSED">Paused</option>
                  <option value="ARCHIVED">Archived</option>
                </select>
                {(statusFilter || searchQuery) && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      setStatusFilter('');
                      setSearchQuery('');
                      setPage(1);
                    }}
                  >
                    Clear
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Workflow List */}
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Card key={i}>
                <CardHeader>
                  <LoadingSkeleton className="h-6 w-3/4" />
                  <LoadingSkeleton className="h-4 w-1/2 mt-2" />
                </CardHeader>
                <CardContent>
                  <LoadingSkeleton className="h-20 w-full" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : error ? (
          <Card>
            <CardContent className="py-12 text-center">
              <div className="text-6xl mb-4">❌</div>
              <CardTitle className="mb-2">Error Loading Workflows</CardTitle>
              <CardDescription>
                {error instanceof Error ? error.message : 'An error occurred'}
              </CardDescription>
              <Button
                className="mt-4"
                variant="outline"
                onClick={() => window.location.reload()}
              >
                Retry
              </Button>
            </CardContent>
          </Card>
        ) : workflows.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <div className="text-6xl mb-4">🔄</div>
              <CardTitle className="mb-2">No Workflows Found</CardTitle>
              <CardDescription className="mb-4">
                {statusFilter || searchQuery
                  ? 'No workflows match your filters. Try adjusting your search.'
                  : 'Get started by creating your first workflow to orchestrate agents.'}
              </CardDescription>
              {!statusFilter && !searchQuery && (
                <Button onClick={() => navigate('/workflows/new')}>
                  Create Your First Workflow
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {workflows.map((workflow) => (
                <WorkflowCard key={workflow.id} workflow={workflow} />
              ))}
            </div>

            {/* Pagination */}
            {meta && meta.pages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-8">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Previous
                </Button>
                <span className="text-sm text-muted-foreground px-4">
                  Page {page} of {meta.pages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(meta.pages, p + 1))}
                  disabled={page === meta.pages}
                >
                  Next
                </Button>
              </div>
            )}
          </>
        )}
        </div>
      </div>
    </div>
  );
}

/**
 * Stats Card Component
 */
interface StatsCardProps {
  title: string;
  value: number | string;
  icon: string;
  color?: 'default' | 'green' | 'gray' | 'yellow' | 'blue' | 'purple';
  isLoading?: boolean;
}

function StatsCard({
  title,
  value,
  icon,
  color = 'default',
  isLoading,
}: StatsCardProps) {
  const colorClasses = {
    default: 'bg-white',
    green: 'bg-green-50 border-green-200',
    gray: 'bg-gray-50 border-gray-200',
    yellow: 'bg-yellow-50 border-yellow-200',
    blue: 'bg-blue-50 border-blue-200',
    purple: 'bg-purple-50 border-purple-200',
  };

  const textColors = {
    default: 'text-gray-900',
    green: 'text-green-700',
    gray: 'text-gray-700',
    yellow: 'text-yellow-700',
    blue: 'text-blue-700',
    purple: 'text-purple-700',
  };

  return (
    <Card className={colorClasses[color]}>
      <CardContent className="pt-4 pb-3">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            {title}
          </span>
          <span>{icon}</span>
        </div>
        {isLoading ? (
          <LoadingSkeleton className="h-8 w-12" />
        ) : (
          <p className={`text-2xl font-bold ${textColors[color]}`}>{value}</p>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Workflow Card Component
 */
interface WorkflowCardProps {
  workflow: Workflow;
}

function WorkflowCard({ workflow }: WorkflowCardProps) {
  const deleteWorkflow = useDeleteWorkflow();
  const activateWorkflow = useActivateWorkflow();
  const pauseWorkflow = usePauseWorkflow();
  const [showRunModal, setShowRunModal] = useState(false);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleToggleStatus = () => {
    if (workflow.status === 'ACTIVE') {
      pauseWorkflow.mutate(workflow.id);
    } else if (workflow.status === 'PAUSED' || workflow.status === 'DRAFT') {
      activateWorkflow.mutate(workflow.id);
    }
  };

  // Open the same modal the detail page uses. This used to fire the workflow
  // immediately with NO input, so a workflow whose agent needs input ran, hit
  // the model with {}, got a polite "please give me the boards" back, and
  // reported success — indistinguishable from a real run in the list.
  const handleRun = () => {
    setShowRunModal(true);
  };

  const handleDelete = () => {
    deleteWorkflow.mutate(workflow.id, {
      onSuccess: () => setShowDeleteConfirm(false),
    });
  };

  const successRate =
    workflow.runCount > 0
      ? Math.round((workflow.successCount / workflow.runCount) * 100)
      : null;

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <span className="text-2xl">{getWorkflowStatusIcon(workflow.status)}</span>
            <div>
              <CardTitle className="text-lg leading-tight">{workflow.name}</CardTitle>
              <div className="flex items-center gap-2 mt-1">
                <span
                  className={`px-2 py-0.5 text-xs rounded-full font-medium ${getWorkflowStatusColor(
                    workflow.status
                  )}`}
                >
                  {workflow.status}
                </span>
              </div>
            </div>
          </div>
          <span className="text-xs text-muted-foreground">v{workflow.version}</span>
        </div>
      </CardHeader>
      <CardContent>
        {workflow.description && (
          <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
            {workflow.description}
          </p>
        )}

        {/* Workflow Info */}
        <div className="grid grid-cols-2 gap-2 text-sm mb-3">
          <div className="flex items-center gap-1">
            <span>{getExecutionModeIcon(workflow.executionMode)}</span>
            <span className="text-muted-foreground">
              {getExecutionModeLabel(workflow.executionMode)}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <span>{getTriggerTypeIcon(workflow.trigger.type)}</span>
            <span className="text-muted-foreground">
              {getTriggerTypeLabel(workflow.trigger.type)}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <span>📦</span>
            <span className="text-muted-foreground">
              {workflow.steps.length} step{workflow.steps.length !== 1 ? 's' : ''}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <span>🚀</span>
            <span className="text-muted-foreground">
              {workflow.runCount} run{workflow.runCount !== 1 ? 's' : ''}
            </span>
          </div>
        </div>

        {/* Success Rate Bar */}
        {successRate !== null && (
          <div className="mb-3">
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-muted-foreground">Success Rate</span>
              <span
                className={`font-medium ${
                  successRate >= 80
                    ? 'text-green-600'
                    : successRate >= 50
                    ? 'text-yellow-600'
                    : 'text-red-600'
                }`}
              >
                {successRate}%
              </span>
            </div>
            <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <div
                className={`h-full transition-all duration-300 ${
                  successRate >= 80
                    ? 'bg-green-500'
                    : successRate >= 50
                    ? 'bg-yellow-500'
                    : 'bg-red-500'
                }`}
                style={{ width: `${successRate}%` }}
              />
            </div>
          </div>
        )}

        {/* Tags */}
        {workflow.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {workflow.tags.slice(0, 3).map((tag) => (
              <span
                key={tag}
                className="px-2 py-0.5 text-xs rounded-full bg-slate-100 text-slate-700"
              >
                {tag}
              </span>
            ))}
            {workflow.tags.length > 3 && (
              <span className="px-2 py-0.5 text-xs rounded-full bg-slate-100 text-slate-700">
                +{workflow.tags.length - 3}
              </span>
            )}
          </div>
        )}

        {/* Last Run */}
        {workflow.lastRunAt && (
          <p className="text-xs text-muted-foreground mb-3">
            Last run:{' '}
            {new Date(workflow.lastRunAt).toLocaleString('en-US', {
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </p>
        )}

        {/* Actions */}
        {showDeleteConfirm ? (
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground flex-1">Delete?</span>
            <Button
              size="sm"
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteWorkflow.isPending}
            >
              {deleteWorkflow.isPending ? 'Deleting...' : 'Yes'}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowDeleteConfirm(false)}
            >
              No
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={handleRun}
              disabled={
                workflow.status !== 'ACTIVE'
              }
              title={
                workflow.status !== 'ACTIVE'
                  ? 'Workflow must be active to run'
                  : 'Run workflow'
              }
            >
              Run
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handleToggleStatus}
              disabled={
                workflow.status === 'ARCHIVED' ||
                activateWorkflow.isPending ||
                pauseWorkflow.isPending
              }
            >
              {workflow.status === 'ACTIVE' ? 'Pause' : 'Activate'}
            </Button>
            <Button size="sm" variant="outline" className="w-full" asChild>
              <Link to={`/workflows/${workflow.id}`} className="flex-1">View</Link>
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowDeleteConfirm(true)}
            >
              Delete
            </Button>
          </div>
        )}
      </CardContent>

      <RunWorkflowModal
        isOpen={showRunModal}
        onClose={() => setShowRunModal(false)}
        workflowId={workflow.id}
        workflowName={workflow.name}
      />
    </Card>
  );
}
